import svgLogo from "common/assets/logo.svg"
import { Dropdown } from "../Dropdown"
import { Button } from "../Button"
import { Menu, ChevronDown, Home, PenSquare } from "lucide-react"
import { AccountMenu } from "../AccountMenu"
import { MobileMenu } from "../MobileMenu"
import { useRegionCode } from "modules/i18n/hooks/useRegionCode"
import { useTranslations } from "modules/i18n/hooks/useTranslations"
import { useRegionConfig } from "modules/i18n/hooks/useRegionConfig"
import { useViewer } from "common/hooks/data/useViewer"
import { usePageProps } from "common/hooks/data/usePageProps"
import type { AppProps } from "common/lib/getAppServerSideProps"
import { useCallback, useState } from "react"
import clsx from "classnames"
import { donationsUrl } from "../donationsUrl"
import { DevMenu } from "common/components/Header/Topbar/DevMenu"

const handleSkipToContentClick = () => {
  const target = document.getElementById("main-content")
  if (target) {
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches

    target.scrollIntoView({
      behavior: prefersReducedMotion ? "auto" : "smooth",
      block: "start",
    })

    // Wait until the scrolling animation is finished before focusing the target element to prevent instantly losing focus again when animating done
    requestAnimationFrame(() => {
      target.focus()
    })
  }
}

interface TopbarProps {
  wide?: boolean
}

export const Topbar = ({ wide }: TopbarProps) => {
  const regionCode = useRegionCode()
  const { editUrl, activeRegions } = usePageProps<AppProps>()
  const t = useTranslations("layout.header")
  const regionConfig = useRegionConfig()
  const { viewer, isAuthor, isEditor } = useViewer()
  const [isActive, setIsActive] = useState(false)
  const user = viewer?.user
  const selectedRegion = activeRegions.find(({ code }) => code == regionCode)
  const remainingRegions = activeRegions.filter(({ code }) => code != regionCode)
  const { stay_informed, donations } = regionConfig

  const containerClasses = clsx(
    "relative mx-auto flex h-10 justify-between p-0! text-black lg:h-12",
    {
      "tc-container": !wide,
      "lg:ml-3": wide,
    },
  )

  const donationButtonClasses = clsx(
    "min-hitbox-44 m-1 items-center rounded-full bg-red-600 px-3 py-1 text-xs font-bold text-white visited:text-white hover:bg-red-500 hover:text-white focus:ring-2 focus:ring-red-600 focus:ring-offset-2 focus:outline-none md:block lg:px-5 lg:py-2",
    {
      block: donations.promoted,
      hidden: !donations.promoted,
    },
  )
  const stayInformedButtonClasses = clsx(
    "min-hitbox-44 m-1 self-end rounded-full bg-black px-3 py-1 text-xs font-bold text-white visited:text-white hover:bg-gray-700 hover:text-white focus:ring-2 focus:ring-black focus:ring-offset-2 focus:outline-none lg:px-5 lg:py-2",
    { "hidden md:block": donations.promoted },
  )

  const regionDropdownOverlay = (
    <>
      {remainingRegions.map(({ code, nameInRegionLanguage }) => (
        <a
          href={`/${code}`}
          key={code}
          role="menuitem"
          className="block w-full px-2.5 py-3.5 text-left text-xs text-black transition hover:bg-gray-50"
        >
          {nameInRegionLanguage}
        </a>
      ))}
    </>
  )

  const handleClose = useCallback(() => {
    setIsActive(false)
    document.getElementById("main-content")?.classList?.remove("max-lg:overflow-hidden")
    setTimeout(() => {
      const mobileMenuButton = document.getElementById("mobile-menu-button")

      if (mobileMenuButton) {
        mobileMenuButton.focus()
      }
    }, 50)
  }, [])

  const toggleMenu = useCallback(() => {
    if (isActive) {
      handleClose()
    } else {
      setIsActive(true)
      // Used to prevent scrolling out the bottom of menu when it is open
      document.getElementById("main-content")?.classList?.add("max-lg:overflow-hidden")
    }
  }, [handleClose, isActive])

  return (
    <nav
      className="relative z-20 border-b border-solid border-gray-200 bg-white"
      data-testid="Topbar"
      aria-label={t("menu")}
    >
      <div className={clsx("relative mx-auto", { "tc-container": !wide })}>
        <button
          type="button"
          onClick={handleSkipToContentClick}
          className="sr-only text-base focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:border-2 focus:border-black focus:bg-white focus:px-4 focus:py-2 focus:font-bold focus:text-black focus:shadow-lg"
        >
          {t("skip_to_content")}
        </button>
      </div>

      <MobileMenu isActive={isActive} onClose={handleClose} />

      <div className="absolute z-30 w-full lg:hidden">
        <button
          id="mobile-menu-button"
          type="button"
          className={clsx("absolute block p-2 text-gray-800 -outline-offset-1 hover:bg-gray-50", {
            invisible: isActive,
          })}
          onClick={toggleMenu}
          aria-label={t("menu")}
          aria-expanded={false}
          aria-controls="mobile-menu-container"
          tabIndex={isActive ? -1 : 0}
        >
          <Menu className="size-6" />
        </button>
      </div>

      <div className={containerClasses}>
        {process.env.NODE_ENV === "development" && <DevMenu />}
        <div className="flex grow flex-row flex-wrap items-center max-lg:overflow-hidden">
          <a
            href={`/${regionCode}`}
            aria-label={t("go_to_homepage")}
            className="ml-10 flex h-full text-gray-700 -outline-offset-1 hover:bg-gray-50 hover:text-gray-700! max-md:grow lg:-ml-3"
          >
            <span className="hidden p-3 lg:block">
              <Home className="size-6" />
            </span>

            <span className="flex h-full grow items-center">
              <img
                src={svgLogo}
                alt=""
                className="mr-1 max-h-5 w-auto min-w-32 grow md:h-5 lg:hidden"
              />
            </span>
          </a>

          <div className="hidden h-full lg:block" data-testid="region-dropdown">
            <div className="relative h-full border-x border-solid border-gray-200">
              <div className="flex h-full items-center">
                <span className="ml-3 font-sans text-xs">{t("edition")}:</span>

                {selectedRegion && (
                  <Dropdown overlay={regionDropdownOverlay}>
                    <Button>
                      <div className="pr-2 font-bold text-red-600">
                        {selectedRegion.nameInRegionLanguage}
                      </div>
                      <ChevronDown className="size-4" />
                    </Button>
                  </Dropdown>
                )}
              </div>
            </div>
          </div>

          <div className="my-1 flex flex-row-reverse sm:flex-row md:mx-2">
            {donations.enabled && (
              <a
                href={donationsUrl({ regionConfig, regionCode, campaign: "topbar" })}
                className={donationButtonClasses}
              >
                {t("donate")}
              </a>
            )}

            {stay_informed.enabled && (
              <a href={stay_informed.topbar.url} className={stayInformedButtonClasses}>
                {t("get_newsletter")}
              </a>
            )}
          </div>
        </div>

        <div className="flex w-auto flex-row items-center border-r">
          <div className="hidden h-full md:flex">
            {isEditor && editUrl && (
              <div className="flex h-full">
                <Button href={editUrl} withDivider>
                  <PenSquare className="w-5 pr-1" />
                  {t("user_navbar.edit_page")}
                </Button>
              </div>
            )}
            {isAuthor && user?.id && (
              <div className="flex h-full">
                <Button href={`/profiles/${user.id}/dashboard`} withDivider>
                  {t("user_navbar.dashboard")}
                </Button>
              </div>
            )}
            {isEditor && user?.id && (
              <div className="flex h-full">
                <Button href={`/editorial/dashboards/${user.id}`} withDivider>
                  {t("user_navbar.dashboard")}
                </Button>
              </div>
            )}
          </div>
          {!user && (
            <div className="hidden h-full md:flex">
              <Button href="/become-an-author" withDivider>
                {t("user_navbar.become_an_author")}
              </Button>
              <Button href={`/sign_up?return_to=/${regionCode}`} withDivider>
                {t("user_navbar.signup_reader")}
              </Button>
            </div>
          )}
          <AccountMenu />
        </div>
      </div>
    </nav>
  )
}
